import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ProLayout } from '@ant-design/pro-components';
import {
  DashboardOutlined,
  UserOutlined,
  AuditOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  LogoutOutlined,
  ApiOutlined,
  ContainerOutlined,
  PictureOutlined,
  DollarOutlined,
  StarOutlined,
  NotificationOutlined,
  AppstoreOutlined,
  SettingOutlined,
  ImportOutlined,
  HeartOutlined,
  ExperimentOutlined,
  FieldTimeOutlined,
  StopOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { Dropdown, Space, theme } from 'antd';
import { useAuth } from '../hooks/useAuth';
import { usePermission } from '../hooks/usePermission';
import { useAuthStore } from '../stores/auth';
import { authApi } from '../api/auth';

interface MenuItem {
  path: string;
  name: string;
  icon: React.ReactNode;
  children?: MenuItem[];
}

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { account } = useAuth();
  const perms = usePermission();
  const logout = useAuthStore((s) => s.logout);
  const [collapsed, setCollapsed] = useState(false);
  const { token } = theme.useToken();

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Best-effort logout
    }
    logout();
    navigate('/login', { replace: true });
  };

  const menuItems: MenuItem[] = [
    {
      path: '/dashboard',
      name: '仪表盘',
      icon: <DashboardOutlined />,
    },
  ];

  if (perms.canManageAccounts) {
    menuItems.push({
      path: '/accounts',
      name: '用户治理',
      icon: <UserOutlined />,
    });
  }

  if (perms.canReview) {
    menuItems.push({
      path: '/reviews',
      name: '审核中心',
      icon: <AuditOutlined />,
    });
  }

  if (perms.canReview) {
    menuItems.push({
      path: '/content-review',
      name: '内容审查',
      icon: <ContainerOutlined />,
    });
  }

  if (perms.canManageReviewTemplates) {
    menuItems.push({
      path: '/review-templates',
      name: '审核模板',
      icon: <FileTextOutlined />,
    });
  }

  if (perms.isSuperAdmin) {
    menuItems.push({
      path: '/admin-accounts',
      name: '内部账号',
      icon: <SafetyCertificateOutlined />,
    });
  }

  if (perms.canViewAuditLogs) {
    menuItems.push({
      path: '/audit-logs',
      name: '审计日志',
      icon: <FileTextOutlined />,
    });
  }

  if (perms.canManageDomainEvents) {
    menuItems.push({
      path: '/operations/domain-events',
      name: '领域事件',
      icon: <ApiOutlined />,
    });
  }

  if (perms.canManageDomainEvents) {
    menuItems.push({
      path: '/operations/scheduled-job-runs',
      name: '定时任务',
      icon: <FieldTimeOutlined />,
    });
  }

  if (perms.canViewAnalytics) {
    menuItems.push({
      path: '/experiments',
      name: '实验配置',
      icon: <ExperimentOutlined />,
    });
  }

  // Operations management
  menuItems.push({
    path: '/banners',
    name: 'Banner管理',
    icon: <PictureOutlined />,
  });

  menuItems.push({
    path: '/announcements',
    name: '公告管理',
    icon: <NotificationOutlined />,
  });

  menuItems.push({
    path: '/deposits',
    name: '押金管理',
    icon: <DollarOutlined />,
  });

  menuItems.push({
    path: '/ratings',
    name: '评价管理',
    icon: <StarOutlined />,
  });

  menuItems.push({
    path: '/finance-leads',
    name: '金融线索',
    icon: <DollarOutlined />,
  });

  menuItems.push({
    path: '/categories',
    name: '分类管理',
    icon: <AppstoreOutlined />,
  });

  menuItems.push({
    path: '/notification-templates',
    name: '通知模板',
    icon: <NotificationOutlined />,
  });

  menuItems.push({
    path: '/system-config',
    name: '系统配置',
    icon: <SettingOutlined />,
  });

  menuItems.push({
    path: '/imports',
    name: '数据导入',
    icon: <ImportOutlined />,
  });

  menuItems.push({
    path: '/system-health',
    name: '系统健康',
    icon: <HeartOutlined />,
  });

  menuItems.push({
    path: '/content-takedown',
    name: '内容下架',
    icon: <StopOutlined />,
  });

  menuItems.push({
    path: '/content-analytics',
    name: '内容统计',
    icon: <BarChartOutlined />,
  });

  const route = {
    path: '/',
    children: menuItems.map((item) => ({
      path: item.path,
      name: item.name,
      icon: item.icon,
      children: item.children?.map((child) => ({
        path: child.path,
        name: child.name,
        icon: child.icon,
      })),
    })),
  };

  return (
    <ProLayout
      title="万企点点通管理后台"
      logo={null}
      layout="mix"
      collapsed={collapsed}
      onCollapse={setCollapsed}
      route={route}
      location={location}
      onMenuHeaderClick={() => navigate('/dashboard')}
      menuItemRender={(item, dom) => <a onClick={() => item.path && navigate(item.path)}>{dom}</a>}
      avatarProps={{
        icon: <UserOutlined />,
        size: 'small',
        title: account?.nickname ?? account?.id ?? 'Admin',
        render: (_props, dom) => (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'role',
                  label: (
                    <Space>
                      <span style={{ color: token.colorTextSecondary }}>角色:</span>
                      <span>{account?.admin_profile?.admin_role ?? '无'}</span>
                    </Space>
                  ),
                  disabled: true,
                },
                {
                  type: 'divider',
                },
                {
                  key: 'logout',
                  icon: <LogoutOutlined />,
                  label: '退出登录',
                  danger: true,
                  onClick: handleLogout,
                },
              ],
            }}
          >
            {dom}
          </Dropdown>
        ),
      }}
      token={{
        header: {
          colorBgHeader: token.colorBgContainer,
        },
        sider: {
          colorMenuBackground: '#111827',
          colorTextMenu: '#cbd5e1',
          colorTextMenuSelected: '#ffffff',
          colorTextMenuItemHover: '#ffffff',
          colorBgMenuItemSelected: '#1f4d8c',
          colorBgMenuItemHover: '#1f2937',
          colorTextMenuSecondary: '#94a3b8',
        },
      }}
    >
      <Outlet />
    </ProLayout>
  );
}
